import { DataTable } from "@/components/data-table/DataTable";
import { DataTableColumnHeader } from "@/components/data-table/DataTableColumnHeader";
import type { OrganizationUserAdminDTO } from "@/orval/model";
import {
  getAdminOwnerGetOwnersFromOrganizationQueryKey,
  adminOwnerGetOwnersFromOrganization,
  getAdminOwnerGetOwnersFromOrganizationQueryOptions,
} from "@/orval/admin-owner";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { EditOrganizationUserDialog } from "./-components/edit-organization-user-dialog";
import { useState } from "react";
import { PortalPageHeader } from "@/components/portal-page-header";
import { searchStringSearchSchema } from "@/lib/route-search";
import { buildDataTableQueryOptions, ensureRouteQueryData } from "@/lib/route-loader";

const columns: ColumnDef<OrganizationUserAdminDTO>[] = [
  {
    accessorKey: "firstName",
    header: ({ column }) => <DataTableColumnHeader column={column} title="First Name" />,
    enableSorting: true,
  },
  {
    accessorKey: "lastName",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Last Name" />,
    enableSorting: true,
  },
  {
    accessorKey: "email",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Email" />,
  },
  {
    accessorKey: "phoneNumber",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Phone Number" />,
  },
];

const OrganizationAdminOwnersPage: React.FC = () => {
  const { organizationId } = Route.useParams();
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<OrganizationUserAdminDTO | null>(null);

  const getOwners = adminOwnerGetOwnersFromOrganization;

  const handleRowClick = (admin: OrganizationUserAdminDTO) => {
    setSelectedUser(admin);
    setDialogOpen(true);
  };

  return (
    <div className="flex flex-1 min-h-0 flex-col p-4 md:p-6">
      <PortalPageHeader className="mb-8" title="Admins" />

      <DataTable
        columns={columns}
        queryFn={(params) => getOwners(organizationId, params)}
        queryKeyFn={(params) => getAdminOwnerGetOwnersFromOrganizationQueryKey(organizationId, params)}
        onRowClick={handleRowClick}
        getRowIdFn={(row) => row.id.toString()}
      />

      {selectedUser && (
        <EditOrganizationUserDialog isOpen={isDialogOpen} onClose={() => setDialogOpen(false)} user={selectedUser} />
      )}
    </div>
  );
};

export const Route = createFileRoute("/_app/organizations/$organizationId/admin/admins")({
  component: OrganizationAdminOwnersPage,
  validateSearch: searchStringSearchSchema,
  loader: ({ params, location, ...loaderContext }) => {
    const { searchString } = searchStringSearchSchema.parse(location.search);
    return ensureRouteQueryData(
      loaderContext,
      getAdminOwnerGetOwnersFromOrganizationQueryOptions(
        params.organizationId,
        buildDataTableQueryOptions({ searchString }),
      ),
    );
  },
});
